import { environment } from 'environments/environment';

/**
 * With `loading=async`, the script's onload fires when the SDK *loader* is ready —
 * the `google.maps` namespace is only populated once each library resolves via
 * `importLibrary`. Components construct `google.maps` objects as soon as they render,
 * so bootstrap must wait for every library the app uses, not just the script tag.
 */
const MAPS_LIBRARIES = ['core', 'maps', 'marker', 'places', 'geocoding', 'geometry', 'drawing', 'routes'];
// Bootstrap is gated on the library imports; a stalled import must degrade to "no Maps",
// not leave the user on a blank page (the symptom PI-1513 fixes).
const MAPS_LIBRARIES_TIMEOUT_MS = 15_000;

interface GoogleMapsGlobal {
	maps?: { importLibrary?: (name: string) => Promise<unknown> };
}

/**
 * Loads the Google Maps JavaScript SDK using a key fetched from server-side
 * configuration, so the key is not hardcoded into the client bundle. Resolves only
 * once the `google.maps` namespace is fully populated. Loading failures are
 * swallowed so the application still starts when Maps is unavailable.
 */
export async function loadGoogleMaps(): Promise<void> {
	try {
		const response = await fetch(`${environment.apiUrl}app-configuration/client`);
		if (!response.ok) return;

		const config = await response.json();
		const apiKey = config?.googleMapsApiKey;
		if (!apiKey) return;

		const scriptLoaded = await new Promise<boolean>((resolve) => {
			const script = document.createElement('script');
			script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
				apiKey
			)}&libraries=places,drawing,geometry,routes&language=en&loading=async`;
			script.async = true;
			script.onload = () => resolve(true);
			script.onerror = () => resolve(false);
			document.head.appendChild(script);
		});
		if (!scriptLoaded) return;

		const importLibrary = ((globalThis as { google?: GoogleMapsGlobal }).google?.maps?.importLibrary ?? null) as
			| ((name: string) => Promise<unknown>)
			| null;
		if (!importLibrary) {
			console.error('[Maps Init] SDK loaded but google.maps.importLibrary is unavailable');
			return;
		}

		// Each library is an independent import: one failure must not abort the others
		// (RULES.md clause 12), otherwise bootstrap would proceed while the rest are still loading.
		const settled = Promise.allSettled(MAPS_LIBRARIES.map((library) => importLibrary(library)));
		let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
		const timedOut = new Promise<null>((resolve) => {
			timeoutHandle = setTimeout(() => resolve(null), MAPS_LIBRARIES_TIMEOUT_MS);
		});
		const results = await Promise.race([settled, timedOut]);
		clearTimeout(timeoutHandle);
		if (results === null) {
			console.error(`[Maps Init] Timed out after ${MAPS_LIBRARIES_TIMEOUT_MS}ms waiting for the Maps libraries`);
			return;
		}
		const failed = MAPS_LIBRARIES.filter((_, index) => results[index].status === 'rejected');
		if (failed.length > 0) {
			console.error('[Maps Init] Failed to import Maps libraries:', failed);
		}
	} catch (error) {
		console.error('[Maps Init] Failed to load Google Maps SDK:', error);
	}
}
